import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { object, string, number } from 'yup';
import { isEmpty } from 'lodash';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Typography } from '@mui/material';

import { useAppSelector } from '../../../store';
import Alert from '../../../components/UI/Alert/Alert';
import { CONSTANTS } from '../../../utils/constants';
import { PERMISSIONS } from '../../../utils/permissions';
import { FieldTypes, IFormFields } from '../../../models/form';
import Form from '../../../components/Container/Form/Form';
import useTranslations from '../../../hooks/useTranslations';
import { IAutoCompleteItem } from '../../../models/autoComplete';
import BackButton from '../../../components/UI/BackButton/BackButton';
import { IPage } from '../../../models/page';
import { ConfigurationActions } from '../../../store/entities/configuration/configuration.actions';
import { GeneralActions } from '../../../store/entities/general/general.actions';
import { PricingPlansActions } from '../../../store/entities/pricingPlans/pricingPlans.action';
import {
  AddPricePlan,
  UpdatePricePlan,
  UpdatePricingsPricingPlan,
} from '../../../store/entities/pricingPlans/type';

import './PricingPlansDetails.scss';
import { IDynamicListOptionItem } from '../../../models/dynamic-lists';
import {
  PricingRequirement,
  subscriptionModelsTypes,
} from '../../../models/pricingPlan';
import { FormikProps } from 'formik';
import { IDynamicList } from '../../../store/entities/general/type';

const PricePlanDetails = ({ permission, nextRoute }: IPage) => {
  const [show, setShow] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<any>({});
  const [validationValues, setValidationValues] = useState<any>({});
  const [validation, setValidation] = useState<any>({});
  const [pricingPeriodsList, setPricingPeriodsList] = useState<
    IAutoCompleteItem[]
  >([]);
  const [subscriptionModelList, setSubscriptionModelList] = useState<
    IAutoCompleteItem[]
  >([]);

  const { appLanguage } = useAppSelector((store) => store.general);
  const { appPermissions }: { appPermissions: string[] } = useAppSelector(
    (store) => store.auth
  );
  const [formikProps, setFormikProps] = useState<FormikProps<any>>();
  const { dynamicListOptionsByKey } = useAppSelector(
    (state) => state.configuration
  );
  const { pricingPeriods } = useAppSelector((state) => state.general);
  const { subscriptionModelsRule, pricePlanDetails, subscriptionModels } =
    useAppSelector((state) => state.pricingPlans);

  const { rcTranslate } = useTranslations();
  const navigate = useNavigate();
  const { pricePlanId = '' } = useParams();
  const [isEditable, setIsEditable] = useState<boolean>(true);
  const dispatch = useDispatch();

  const updateField = useCallback(
    async (field: string, value: any) => {
      await formikProps?.setFieldValue(field, value);
      await formikProps?.setFieldTouched(field, true, true);
    },
    [formikProps]
  );

  const clearField = useCallback(
    async (field: string) => {
      await updateField(field, null);
    },
    [formikProps, updateField]
  );

  let navigationTimeout: any;

  const getSelectedSubscriptionModel = (): number | null => {
    return (
      formikProps?.values?.subscriptionModel ??
      pricePlanDetails?.subscriptionModel ??
      null
    );
  };

  const getSubscriptionModelRule = () => {
    const subscriptionModel = getSelectedSubscriptionModel();
    if (!subscriptionModel || !subscriptionModelsRule) {
      return null;
    }
    const subscriptionModelType =
      subscriptionModelsTypes[
        subscriptionModel as keyof typeof subscriptionModelsTypes
      ];
    if (!subscriptionModelType) {
      return null;
    }
    return subscriptionModelsRule.subscriptionModels[subscriptionModelType];
  };
  const hasRequirement = (requirement: PricingRequirement): boolean => {
    const rule = getSubscriptionModelRule();
    return rule ? rule.requirements.includes(requirement) : false;
  };

  const isOneTimePaymentRequired = (): boolean => {
    return hasRequirement(PricingRequirement.OneTimePayment);
  };

  const isCellSizesUsagePricingsNecessary = (): boolean => {
    return hasRequirement(PricingRequirement.CellSizesRegularUsagePricings);
  };

  const isCellSizesInitialPricingsNecessary = (): boolean => {
    return hasRequirement(PricingRequirement.CellSizesInitialPricings);
  };

  const isCellSizesOveragePricingsNecessary = (): boolean => {
    return hasRequirement(PricingRequirement.CellSizesOveragePricings);
  };

  useEffect(() => {
    return () => {
      clearTimeout(navigationTimeout);
      setShow(false);
      dispatch(
        PricingPlansActions.setPricePlanData({ pricePlanDetails: null })
      );
    };
  }, [navigationTimeout, dispatch]);

  useEffect(() => {
    if (permission === false) {
      navigate(nextRoute);
    }
  }, [permission, nextRoute, navigate]);

  useEffect(() => {
    dispatch(PricingPlansActions.getSubscriptionModelsRules());
    dispatch(PricingPlansActions.getSubscriptionModels());
    dispatch(GeneralActions.getPricingPeriods());
    dispatch(
      ConfigurationActions.getDynamicListOptionsByKey(
        CONSTANTS.DYNAMIC_LIST.KEYS.CELL_SIZES
      )
    );
  }, [dispatch]);

  useEffect(() => {
    if (pricePlanId) {
      dispatch(PricingPlansActions.getPricingPlanDetails(pricePlanId));
    }
  }, [dispatch, pricePlanId]);

  useEffect(() => {
    if (dynamicListOptionsByKey?.length || pricePlanDetails?.pricings?.length) {
      let initialValues = {};
      let validationValues = {
        title: string().required(CONSTANTS.VALIDATION_ERRORS.REQUIRED),
        subscriptionModel: number()
          .typeError(CONSTANTS.VALIDATION_ERRORS.REQUIRED)
          .required(CONSTANTS.VALIDATION_ERRORS.REQUIRED),
        marketingName: string()
          .typeError(CONSTANTS.VALIDATION_ERRORS.REQUIRED)
          .required(CONSTANTS.VALIDATION_ERRORS.REQUIRED),
        pricingPeriod: number()
          .typeError(CONSTANTS.VALIDATION_ERRORS.REQUIRED)
          .required(CONSTANTS.VALIDATION_ERRORS.REQUIRED),
      };

      if (isOneTimePaymentRequired()) {
        Object.assign(validationValues, {
          price: number()
            .typeError(CONSTANTS.VALIDATION_ERRORS.REQUIRED)
            .required(CONSTANTS.VALIDATION_ERRORS.REQUIRED),
        });
      } else {
        if ('price' in validationValues) {
          delete validationValues.price;
        }
      }

      const assignInitialValues = (
        index: number,
        initialPriceValue: number | null,
        unitPriceValue: number | null,
        unitOveragePriceValue: number | null
      ) => {
        const initialPriceKey = `InitialPrice_${index}`;
        const unitPriceKey = `UnitPrice_${index}`;
        const unitOveragePriceKey = `UnitOveragePrice_${index}`;
        Object.assign(
          initialValues,
          { [initialPriceKey]: initialPriceValue },
          { [unitPriceKey]: unitPriceValue },
          { [unitOveragePriceKey]: unitOveragePriceValue }
        );
      };

      const assignValidationValues = (index: number) => {
        const initialPriceKey = `InitialPrice_${index}`;
        const unitPriceKey = `UnitPrice_${index}`;
        const unitOveragePriceKey = `UnitOveragePrice_${index}`;

        if (isCellSizesInitialPricingsNecessary()) {
          Object.assign(validationValues, {
            [initialPriceKey]: number()
              .typeError(CONSTANTS.VALIDATION_ERRORS.REQUIRED)
              .required(CONSTANTS.VALIDATION_ERRORS.REQUIRED),
          });
        }

        if (isCellSizesUsagePricingsNecessary()) {
          Object.assign(validationValues, {
            [unitPriceKey]: number()
              .typeError(CONSTANTS.VALIDATION_ERRORS.REQUIRED)
              .required(CONSTANTS.VALIDATION_ERRORS.REQUIRED),
          });
        }

        if (isCellSizesOveragePricingsNecessary()) {
          Object.assign(validationValues, {
            [unitOveragePriceKey]: number()
              .typeError(CONSTANTS.VALIDATION_ERRORS.REQUIRED)
              .required(CONSTANTS.VALIDATION_ERRORS.REQUIRED),
          });
        }
      };

      if (pricePlanDetails?.pricings?.length) {
        for (let i = 0; i < pricePlanDetails?.pricings?.length; i++) {
          const { initialPrice, unitPrice, unitOveragePrice } =
            pricePlanDetails?.pricings[i];
          assignInitialValues(i, initialPrice, unitPrice, unitOveragePrice);
          assignValidationValues(i);
        }
        Object.assign(
          initialValues,
          { title: pricePlanDetails?.title },
          { marketingName: pricePlanDetails?.marketingName },
          { subscriptionModel: pricePlanDetails?.subscriptionModel },
          { pricingPeriod: pricePlanDetails?.pricingPeriod },
          { price: pricePlanDetails?.price },
          { isMegurimDefault: pricePlanDetails?.isMegurimDefault || false }
        );
      } else {
        for (let i = 0; i < dynamicListOptionsByKey?.length; i++) {
          assignInitialValues(i, null, null, null);
          assignValidationValues(i);
        }
        Object.assign(
          initialValues,
          { title: null },
          { subscriptionModel: null },
          { pricingPeriod: null },
          { isMegurimDefault: false }
        );
      }

      setInitialValues(initialValues);
      setValidationValues(validationValues);
    }
  }, [
    appLanguage,
    dynamicListOptionsByKey,
    pricePlanDetails,
    formikProps?.values,
  ]);

  useEffect(() => {
    if (!isEmpty(validationValues)) {
      const pricePlanDetailsValidation = object().shape(validationValues);
      setValidation(pricePlanDetailsValidation);
      setIsEditable(!pricePlanDetails?.replacedBy);
    }
  }, [validationValues]);

  useEffect(() => {
    if (subscriptionModels.length) {
      const subscriptionModel: IAutoCompleteItem[] = subscriptionModels.map(
        (district: IDynamicListOptionItem) => ({
          id: district?.id,
          label:
            appLanguage === CONSTANTS.DEFAULT_LANGUAGE
              ? district.nameHE
              : (district.nameEN ?? ''),
        })
      );
      setSubscriptionModelList(subscriptionModel);
    }
  }, [appLanguage, subscriptionModels]);

  // handle when the pricingPeriod change
  const handleClearWhenPricingPeriodChange = useCallback(async () => {
    // Reset price and other dependent fields
    await clearField('price');
    for (const index in dynamicListOptionsByKey) {
      await clearField(`InitialPrice_${index}`);
      await clearField(`UnitPrice_${index}`);
      await clearField(`UnitOveragePrice_${index}`);
    }
  }, []);

  // Every time subscriptionModel type is changed in the select field, we need to get the correct pricingPeriod
  useEffect(() => {
    const subTouched = formikProps?.touched?.subscriptionModel;
    if (subTouched) {
      handleClearWhenPricingPeriodChange();
      clearField('pricingPeriod');
    }

    const rule = getSubscriptionModelRule();
    if (!rule || !pricingPeriods?.length) {
      setPricingPeriodsList([]);
      return;
    }

    // Filter pricing periods based on allowed periods for the subscription model
    const allowedPricingPeriods = pricingPeriods.filter(
      (period: IDynamicList) =>
        rule.allowedPricingPeriods.includes(Number(period.id))
    );

    const pricingList: IAutoCompleteItem[] = allowedPricingPeriods.map(
      (pricingPlan: any) => ({
        id: pricingPlan.id,
        label:
          appLanguage === CONSTANTS.DEFAULT_LANGUAGE
            ? pricingPlan.nameHE
            : (pricingPlan.nameEN ?? ''),
      })
    );
    setPricingPeriodsList(pricingList);
  }, [
    formikProps?.values?.subscriptionModel,
    pricePlanDetails,
    subscriptionModelsRule,
    pricingPeriods,
    appLanguage,
  ]);

  // Every time pricingPeriod type is changed in the select field, we need to clear the price and pricings
  useEffect(() => {
    const ppTouched = formikProps?.touched?.pricingPeriod;
    if (ppTouched) {
      handleClearWhenPricingPeriodChange();
    }
  }, [formikProps?.values?.pricingPeriod]);

  const hasCells =
    isCellSizesInitialPricingsNecessary() ||
    isCellSizesOveragePricingsNecessary() ||
    isCellSizesUsagePricingsNecessary();

  const fields: IFormFields = useMemo(() => {
    return {
      initialValues: initialValues,
      formData: [
        {
          type: FieldTypes.TEXT,
          id: 'title',
          label: rcTranslate(
            'pricingPlans.pricingPlanDetails.fieldNames.title'
          ),
          props: {
            disabled: !isEditable,
          },
        },
        {
          type: FieldTypes.TEXT,
          id: 'marketingName',
          label: 'pricingPlans.pricingPlanDetails.fieldNames.marketingName',
          props: {
            disabled: !isEditable,
          },
        },
        {
          type: FieldTypes.AUTOCOMPLETE,
          id: 'subscriptionModel',
          label: rcTranslate(
            'pricingPlans.pricingPlanDetails.fieldNames.subscriptionModel'
          ),
          items: subscriptionModelList,
          props: {
            multiple: false,
            disabled: !isEditable || pricePlanId,
          },
        },
        {
          type: FieldTypes.AUTOCOMPLETE,
          id: 'pricingPeriod',
          label: rcTranslate(
            'pricingPlans.pricingPlanDetails.fieldNames.pricingPeriod'
          ),
          items: pricingPeriodsList,
          props: {
            multiple: false,
            disabled:
              !isEditable ||
              (formikProps?.values?.subscriptionModel ||
              pricePlanDetails?.subscriptionModel
                ? false
                : true),
          },
        },
        {
          type: FieldTypes.SWITCH,
          id: 'isMegurimDefault',
          label:
            'pricingPlans.pricingPlanDetails.fieldNames.isResidentialDefault',
          className: 'pricingPlans__defaultSwitch',
          disabled: !isEditable || pricePlanDetails?.isMegurimDefault,
        },
        {
          type: FieldTypes.COMPONENT,
          id: 'isResidentialDefaultInfo',
          label: '',
          component: () => (
            <Box className="pricingPlans__defaultInfo">
              <InfoOutlinedIcon className="pricingPlans__defaultInfoIcon" />
              <Typography variant="caption">
                {pricePlanDetails?.isMegurimDefault
                  ? rcTranslate(
                      'pricingPlans.pricingPlanDetails.fieldNames.isResidentialDefaultEditNotAllowed'
                    )
                  : rcTranslate(
                      'pricingPlans.pricingPlanDetails.fieldNames.isResidentialDefaultInfo'
                    )}
              </Typography>
            </Box>
          ),
        },
        ...(isOneTimePaymentRequired()
          ? [
              {
                type: FieldTypes.NUMBER,
                id: 'price',
                label: 'pricingPlans.pricingPlanDetails.fieldNames.price',
                props: {
                  disabled: !isEditable,
                },
              },
            ]
          : []),
        ...(hasCells
          ? [
              {
                type: FieldTypes.SUBTITLE,
                id: 'pricingText',
                label: rcTranslate(
                  'pricingPlans.pricingPlanDetails.fieldNames.pricing'
                ),
                props: {
                  disabled: !isEditable,
                },
              },
              {
                type: FieldTypes.CHILDREN,
                id: 'pricings',
                label: '',
                props: {
                  disabled: !isEditable,
                },
                fields: dynamicListOptionsByKey
                  ?.sort((a, b) => a.id - b.id)
                  ?.map((pricing, index) => ({
                    type: FieldTypes.CHILDREN,
                    id: `pricing ${pricing.id}`,
                    label: '',
                    props: {
                      disabled: !isEditable,
                    },
                    fields: [
                      {
                        type: FieldTypes.FORM_CONTROL,
                        id: `CellSize_${index}`,
                        label: rcTranslate(
                          'pricingPlans.pricingPlanDetails.fieldNames.cellSize',
                          [
                            {
                              label: 'cellSize',
                              value:
                                appLanguage === CONSTANTS.DEFAULT_LANGUAGE
                                  ? pricing.nameHE
                                  : (pricing.nameEn ?? ''),
                            },
                          ]
                        ),
                        props: {
                          disabled: !isEditable,
                        },
                        className: 'pricingPlans__cellSize',
                      },
                      ...(isCellSizesInitialPricingsNecessary()
                        ? [
                            {
                              type: FieldTypes.NUMBER,
                              id: `InitialPrice_${index}`,
                              label:
                                'pricingPlans.pricingPlanDetails.fieldNames.initialPrice',
                              props: {
                                disabled: !isEditable,
                              },
                            },
                          ]
                        : []),
                      ...(isCellSizesUsagePricingsNecessary()
                        ? [
                            {
                              type: FieldTypes.NUMBER,
                              id: `UnitPrice_${index}`,
                              label:
                                'pricingPlans.pricingPlanDetails.fieldNames.unitPrice',
                              props: {
                                disabled: !isEditable,
                              },
                            },
                          ]
                        : []),
                      ...(isCellSizesOveragePricingsNecessary()
                        ? [
                            {
                              type: FieldTypes.NUMBER,
                              id: `UnitOveragePrice_${index}`,
                              label:
                                'pricingPlans.pricingPlanDetails.fieldNames.unitOveragePrice',
                              props: {
                                disabled: !isEditable,
                              },
                            },
                          ]
                        : []),
                    ],
                    className: 'pricingPlans__wrapper',
                  })),
              },
            ]
          : []),
      ],
    };
  }, [
    initialValues,
    formikProps?.values,
    pricingPeriodsList,
    pricePlanDetails,
    subscriptionModelsRule,
    subscriptionModelList,
    dynamicListOptionsByKey,
    isEditable,
    pricePlanId,
  ]);

  const getIsInPricings = (cellSize: number) => {
    return pricePlanDetails?.pricings?.find(
      (pd: any) => pd.cellSize === cellSize
    );
  };

  const updatePricePlan = async (
    values: any,
    setSubmitting: (value: boolean) => void
  ) => {
    if (!isEditable) {
      setSubmitting(false);
      return;
    }
    let formValues = { ...values };
    const pricings: UpdatePricingsPricingPlan[] = dynamicListOptionsByKey?.map(
      (cellSize, index) => {
        const price = getIsInPricings(cellSize.id);
        return {
          cellSize: price?.CellSize || cellSize?.id,
          id: price?.id || null,
          initialPrice: Number(formValues[`InitialPrice_${index}`]),
          unitPrice: Number(formValues[`UnitPrice_${index}`]),
          unitOveragePrice: Number(formValues[`UnitOveragePrice_${index}`]),
        };
      }
    );
    let updatePricingPlanValues: UpdatePricePlan = {
      title: formValues.title,
      marketingName: formValues.marketingName,
      pricingPeriod: formValues.pricingPeriod,
      price: formValues.price ? Number(formValues.price) : undefined,
      pricings,
      isMegurimDefault: formValues.isMegurimDefault || false,
    };

    const res: any = await dispatch(
      PricingPlansActions.updatePricePlan(updatePricingPlanValues, pricePlanId)
    );
    setSubmitting(false);
    if (res?.isSucceeded) {
      setShow(true);
      navigationTimeout = setTimeout(() => {
        navigate(
          `/${CONSTANTS.ROUTES.PRICING_PLANS_MANAGEMENT.BASE}/${CONSTANTS.ROUTES.PRICING_PLANS_MANAGEMENT.CHILDREN.PRICING_PLANS_LIST}`
        );
      }, 5000);
    }
  };

  const addNewPricePlan = async (
    values: any,
    setSubmitting: (value: boolean) => void
  ) => {
    let formValues = { ...values };
    let pricings: any = dynamicListOptionsByKey?.map((cellSize, index) => ({
      CellSize: cellSize?.id,
      InitialPrice: isCellSizesInitialPricingsNecessary()
        ? Number(formValues[`InitialPrice_${index}`])
        : undefined,
      UnitPrice: isCellSizesUsagePricingsNecessary()
        ? Number(formValues[`UnitPrice_${index}`])
        : undefined,
      UnitOveragePrice: isCellSizesOveragePricingsNecessary()
        ? Number(formValues[`UnitOveragePrice_${index}`])
        : undefined,
    }));
    let addPricingPlanValues: AddPricePlan = {
      title: formValues.title,
      marketingName: formValues.marketingName,
      subscriptionModel: formValues.subscriptionModel,
      pricingPeriod: formValues.pricingPeriod,
      price: isOneTimePaymentRequired() ? Number(formValues.price) : undefined,
      isMegurimDefault: formValues.isMegurimDefault || false,
      pricings,
    };

    const res: any = await dispatch(
      PricingPlansActions.addPricePlan(addPricingPlanValues)
    );
    setSubmitting(false);
    if (res?.isSucceeded) {
      setShow(true);
      navigationTimeout = setTimeout(() => {
        navigate(
          '/' +
            CONSTANTS.ROUTES.PRICING_PLANS_MANAGEMENT.BASE +
            '/' +
            CONSTANTS.ROUTES.PRICING_PLANS_MANAGEMENT.CHILDREN
              .PRICING_PLANS_LIST
        );
      }, 5000);
    }
  };

  const getFormikProps = useCallback((values: FormikProps<any>) => {
    setFormikProps(values);
  }, []);

  return (
    <>
      <BackButton />

      <Form
        fields={fields}
        formTitle={[
          pricePlanId
            ? isEditable
              ? 'pricingPlans.pricingPlanDetails.pageTitle.update'
              : 'pricingPlans.pricingPlanDetails.pageTitle.view'
            : 'pricingPlans.pricingPlanDetails.pageTitle.add',
        ]}
        formValidation={validation}
        onSubmit={pricePlanId ? updatePricePlan : addNewPricePlan}
        submitButtonTitle="button.save"
        isDisabled={
          !appPermissions.includes(
            pricePlanId
              ? PERMISSIONS.UPDATE_PRICING_PLAN
              : PERMISSIONS.ADD_PRICING_PLAN
          )
        }
        showSubmitButton={isEditable}
        getFormikProps={getFormikProps}
      />

      {show && (
        <Alert
          message={rcTranslate(
            pricePlanId
              ? 'pricingPlans.pricingPlanDetails.updateSuccess'
              : 'pricingPlans.pricingPlanDetails.addSuccess'
          )}
          color="success"
        />
      )}
    </>
  );
};

export default PricePlanDetails;
